import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '../components/Header';
import CallbackModal from '../components/CallbackModal';
import NotFoundPage from './NotFoundPage';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../utils/usePageMeta';
import { formatPrice } from '../services/monobankPayment';
import toolsSnapshot from '../data/tools-snapshot.json';
import toolLandingPages, { findToolLandingPageBySlug, getRentalDescription, getToolName } from '../data/toolLandingPages';
import StickyCtaBar from '../components/landing/StickyCtaBar';
import ReviewPrompt from '../components/landing/ReviewPrompt';
import MoreToolsLinks from '../components/landing/MoreToolsLinks';
import { absoluteUrl } from '../utils/langPath';
import { PHONE, PHONE_DISPLAY, buildLocalBusiness, buildRentalService, jsonLd } from '../utils/schema';
import { trackPhoneClick } from '../utils/tracking';

export default function ToolLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, lp } = useLanguage();
  const [showCallback, setShowCallback] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);

  // Opens the callback modal; `term` (e.g. "Доба — 500 грн") is forwarded to Telegram
  const openCallback = (term = null) => {
    setSelectedTerm(term);
    setShowCallback(true);
  };

  const landingConfig = findToolLandingPageBySlug(slug);
  const tool = landingConfig ? toolsSnapshot.find((tl) => tl.id === landingConfig.toolId) : null;

  const canonical = absoluteUrl(lp(`/rent/${slug}`));
  const toolName = getToolName(tool, language);
  const rentName = landingConfig?.rentName?.[language] || toolName;
  const description = getRentalDescription(tool, language);
  const fillPrice = (text) => text.replaceAll('{dayPrice}', tool.pricePerDay);
  const recommended = (tool?.recommendedToolIds || [])
    .map((id) => toolsSnapshot.find((tl) => tl.id === id))
    .filter(Boolean);

  const metaTitle = tool
    ? (language === 'ru'
        ? `Аренда ${rentName} в Харькове — от ${tool.pricePerDay} грн/сутки`
        : `Оренда ${rentName} у Харкові — від ${tool.pricePerDay} грн/доба`)
    : (language === 'ru' ? 'Страница не найдена | kh-tools.com' : 'Сторінку не знайдено | kh-tools.com');
  const metaDescription = tool
    ? (language === 'ru'
        ? `Аренда ${rentName} без залога: ${tool.pricePerDay} грн/сутки, ${tool.pricePerWeek} грн/неделя. Доставка по Харькову. Тел: ${PHONE_DISPLAY}`
        : `Оренда ${rentName} без застави: ${tool.pricePerDay} грн/доба, ${tool.pricePerWeek} грн/тиждень. Доставка по Харкову. Тел: ${PHONE_DISPLAY}`
      ).slice(0, 160)
    : '';

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    path: `/rent/${slug}`,
    lang: language,
    keywords: landingConfig?.keywords?.[language],
    image: tool?.image,
    noindex: !tool,
  });

  if (!landingConfig || !tool) {
    return <NotFoundPage />;
  }

  const h1 = language === 'ru' ? `Аренда ${rentName} в Харькове` : `Оренда ${rentName} у Харкові`;

  const prices = [
    // 4h row temporarily hidden — restore the commented line to bring it back
    // { label: t('rentPage.per4h'), value: tool.pricePer4Hours, term: t('toolLandingPage.term4h') },
    { label: t('rentPage.perDay'), value: tool.pricePerDay, term: t('toolLandingPage.termDay'), wide: true },
    { label: t('rentPage.perNight'), value: tool.pricePerNight, term: t('toolLandingPage.termNight') },
    { label: t('rentPage.perWeek'), value: tool.pricePerWeek, term: t('toolLandingPage.termWeek') },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Rental Service + Offer schema ────────────────────────────────── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={jsonLd(buildRentalService({ tool, name: h1, description, url: canonical, language }))}
      />
      {/* ── LocalBusiness schema ─────────────────────────────────────────── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(buildLocalBusiness(language))} />
      {/* ── FAQPage schema ───────────────────────────────────────────────── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: landingConfig.faq.map(({ q, a }) => ({
            '@type': 'Question',
            name: q[language] || q.uk,
            acceptedAnswer: { '@type': 'Answer', text: fillPrice(a[language] || a.uk) },
          })),
        }) }}
      />
      {/* ── BreadcrumbList schema ────────────────────────────────────────── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: t('toolLandingPage.breadcrumbHome'), item: absoluteUrl(lp('/')) },
            { '@type': 'ListItem', position: 2, name: toolName, item: canonical },
          ],
        }) }}
      />

      <Header
        title={toolName}
        showBack
        onBack={() => {
          if (location.state?.fromApp) {
            navigate(-1);
          } else {
            navigate(lp('/'));
          }
        }}
      />

      <main className="px-4 py-6 max-w-[600px] xl:max-w-[900px] mx-auto animate-fade-in pb-28 xl:pb-10">
        {/* ── Visible breadcrumbs ──────────────────────────────────────── */}
        <nav aria-label="breadcrumb" className="text-[12px] text-gray-400 mb-4">
          <Link to={lp('/')} className="underline">{t('toolLandingPage.breadcrumbHome')}</Link>
          <span className="mx-1.5">/</span>
          <span aria-current="page" className="text-gray-500">{toolName}</span>
        </nav>

        <h1 className="text-[24px] xl:text-[32px] font-bold text-black m-0 mb-1">
          {h1}
        </h1>
        <p className="text-sm xl:text-base text-dewalt font-semibold m-0 mb-5">
          {language === 'ru' ? 'Без залога, с доставкой по городу' : 'Без застави, з доставкою по місту'}
        </p>

        {/* ── Hero: image + price side by side on desktop ─────────────── */}
        <div className="xl:flex xl:gap-10 xl:items-start">
          <img
            src={tool.image}
            alt={language === 'ru' ? `${toolName} в аренду` : `${toolName} в оренду`}
            width="380"
            height="380"
            loading="eager"
            fetchpriority="high"
            className="w-full max-h-[280px] xl:max-h-none xl:w-[380px] xl:h-[380px] xl:flex-shrink-0 object-contain rounded-2xl mb-6 xl:mb-0"
          />

          {/* ── Price ──────────────────────────────────────────────────── */}
          <section className="mb-8 xl:mb-0 xl:flex-1">
            <h2 className="text-lg font-bold text-black mb-3">{t('toolLandingPage.priceTitle')}</h2>
            <div className="grid grid-cols-2 gap-3">
              {prices.map(({ label, value, term, wide }) => {
                const open = () => openCallback(`${term} — ${value} грн`);
                return (
                  <div
                    key={label}
                    role="button"
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={(e) => e.key === 'Enter' && open()}
                    className={`bg-[#fffbf0] border border-dewalt/40 rounded-xl p-3 text-center cursor-pointer active:scale-95 transition-transform hover:bg-[#fff6dc] ${wide ? 'col-span-2' : ''}`}
                  >
                    <div className="text-[18px] font-extrabold text-black">{value} <span className="text-[12px] font-semibold text-gray-500">{label}</span></div>
                  </div>
                );
              })}
            </div>
            <p
              className="flex items-center justify-center gap-1.5 text-[12px] mt-2 mb-4 text-center font-semibold"
              style={{ color: tool.available ? '#16a34a' : '#dc2626' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
              {tool.available ? t('toolLandingPage.availableNow') : t('toolLandingPage.unavailableNow')}
            </p>
            <button
              onClick={() => openCallback()}
              className="w-full bg-dewalt text-black rounded-2xl px-7 py-4 border-none cursor-pointer shadow-lg transition-all duration-150 active:scale-[0.97] hover:shadow-xl font-extrabold tracking-wide"
            >
              {t('toolLandingPage.rentButton')}
            </button>
            <a
              href={`tel:${PHONE}`}
              onClick={() => trackPhoneClick('landing_price')}
              className="block text-center text-[13px] text-black/50 hover:text-black/70 no-underline mt-3"
            >
              {t('toolLandingPage.orCall')} <span className="font-semibold text-black">{PHONE_DISPLAY}</span>
            </a>
          </section>
        </div>

        {/* ── Description ──────────────────────────────────────────────── */}
        <section className="mb-8">
          <h2 className="text-lg font-bold text-black mb-2">{t('toolLandingPage.specsTitle')}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed m-0">
            {description}
          </p>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="mb-8" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-lg font-bold text-black mb-3">{t('toolLandingPage.faqTitle')}</h2>
          <div className="flex flex-col gap-2">
            {landingConfig.faq.map(({ q, a }, i) => (
              <details key={i} className="group border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-4 py-3 font-semibold text-[14px] text-black cursor-pointer bg-white list-none [&::-webkit-details-marker]:hidden">
                  <span>{q[language] || q.uk}</span>
                  <span className="ml-2 text-dewalt transition-transform duration-200 group-open:rotate-180" aria-hidden="true">▼</span>
                </summary>
                <div className="px-4 pb-3 text-[13px] text-gray-600 leading-relaxed bg-gray-50">
                  {fillPrice(a[language] || a.uk)}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* ── Recommended tools row — same card style as /tool/:vmId/:toolId ── */}
        {recommended.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-bold text-black mb-3">{t('toolLandingPage.relatedTitle')}</h2>
            <div className="flex gap-3 overflow-x-auto py-1 -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
              {recommended.map((r) => {
                const rName = getToolName(r, language);
                const rSlug = toolLandingPages[r.id]?.slug;
                const card = (
                  <div className="bg-white rounded-[14px] shadow-[0_4px_16px_rgba(0,0,0,0.12)] overflow-hidden flex flex-col h-full">
                    <div className="relative w-full flex-1 min-h-[140px] bg-white overflow-hidden">
                      <img
                        src={r.image}
                        alt={rName}
                        loading="lazy"
                        className="w-full h-full md:max-h-[190px] object-contain p-2.5"
                        onError={(e) => { e.target.src = '/images/placeholder-tool.svg'; }}
                      />
                    </div>
                    <div className="px-3.5 pt-3 pb-4">
                      <h3 className="text-base font-semibold text-gray-900 m-0 mb-3 leading-tight line-clamp-2">{rName}</h3>
                      <div className="flex gap-3 flex-wrap">
                        {/* 4h price temporarily hidden */}
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-lg font-bold text-dewalt">{formatPrice(r.pricePerDay)}</span>
                          <span className="text-sm text-gray-500">{t('tool.perDay')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <div key={r.id} className="flex-shrink-0 w-[200px]">
                    {rSlug ? (
                      <Link to={lp(`/rent/${rSlug}`)} state={{ fromApp: true }} className="block no-underline">{card}</Link>
                    ) : card}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <MoreToolsLinks excludeIds={[tool.id, ...recommended.map((r) => r.id)]} />

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <section className="bg-dewalt rounded-2xl px-5 py-7 text-center">
          <h2 className="text-lg font-bold text-black m-0 mb-2">{t('toolLandingPage.ctaTitle')}</h2>
          <p className="text-black/70 text-sm m-0 mb-5">{t('toolLandingPage.ctaSubtitle')}</p>
          <button
            onClick={() => openCallback()}
            className="w-full max-w-[280px] bg-black text-dewalt rounded-2xl px-7 py-4 border-none cursor-pointer shadow-lg transition-all duration-150 active:scale-[0.97] hover:shadow-xl font-extrabold tracking-wide"
          >
            {t('rentPage.callbackBtn')}
          </button>
        </section>

        <div className="mt-8">
          <ReviewPrompt placement="landing" />
        </div>

        <div className="mt-6 text-center">
          <Link to={lp('/')} className="text-black/50 underline text-sm">
            {t('toolLandingPage.backToHome')}
          </Link>
        </div>
      </main>

      <CallbackModal isOpen={showCallback} onClose={() => setShowCallback(false)} toolName={toolName} rentalTerm={selectedTerm} leadValue={tool.pricePerDay} />
      <StickyCtaBar onCallback={() => openCallback()} placement="landing_sticky" />
    </div>
  );
}
